/** Keys and helpers for session teardown + POST /auth/logout (API Gateway). */

export const STORAGE_KEYS = {
    TOKEN: "wings_token",
    REFRESH: "wings_refreshToken",
    FEDERATION: "wings_federation",
    REMEMBER_ME: "wings_remember_me",
    EMAIL: "wings_email",
    PASSWORD: "wings_password",
} as const;

/** Set in sessionStorage before redirect to Hosted UI; promoted to FEDERATION on callback. */
export const OAUTH_FEDERATION_SESSION_KEY = "wings_oauth_federation";

/** Set antes de `location.assign("/")` no logout; na próxima carga evita MainLoader duplicado na checagem de token. */
export const POST_LOGOUT_RETURN_SESSION_KEY = "wings_post_logout_return";

export type Federation = "google" | "apple";

export function getStoredFederation(): Federation | null {
    const v = localStorage.getItem(STORAGE_KEYS.FEDERATION);
    if (v === "google" || v === "apple") return v;
    return null;
}

export function promotePendingOAuthFederation(): void {
    const pending = sessionStorage.getItem(OAUTH_FEDERATION_SESSION_KEY);
    if (pending === "google" || pending === "apple") {
        localStorage.setItem(STORAGE_KEYS.FEDERATION, pending);
        sessionStorage.removeItem(OAUTH_FEDERATION_SESSION_KEY);
    }
}

export function clearAuthStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH);
    localStorage.removeItem(STORAGE_KEYS.FEDERATION);
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    localStorage.removeItem(STORAGE_KEYS.EMAIL);
    localStorage.removeItem(STORAGE_KEYS.PASSWORD);
    sessionStorage.removeItem(OAUTH_FEDERATION_SESSION_KEY);
}

export async function requestLogoutApi(opts: {
    refreshToken: string | null;
    accessToken: string | null;
    federation: Federation;
}): Promise<{ hostedUrl: string | null }> {
    const { refreshToken, accessToken, federation } = opts;
    if (!refreshToken && !accessToken) {
        return { hostedUrl: null };
    }

    const body: Record<string, unknown> = { federation };
    if (refreshToken) body.refresh_token = refreshToken;
    if (accessToken) body.access_token = accessToken;

    const oauthClientId =
        federation === "apple"
            ? import.meta.env.VITE_COGNITO_CLIENTID_APPLE
            : import.meta.env.VITE_COGNITO_CLIENTID;
    if (oauthClientId) body.client_id = oauthClientId;

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const base = String(import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
    const res = await fetch(`${base}/auth/logout`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
            (err as { message?: string })?.message ?? `Logout failed: ${res.status}`
        );
    }

    const json = (await res.json()) as {
        data?: { hosted_ui_logout_url?: string | null };
    };
    const hostedUrl = json?.data?.hosted_ui_logout_url ?? null;
    return { hostedUrl };
}
